import { ThemeContext } from '@/state/Theme';
import ButtonCircle from '@/components/ButtonCircle';
import { PinIcon } from '@/icons';
import FacebookIcon from '@/icons/FacebookIcon';
import InstagramIcon from '@/icons/InstagramIcon';
import TwitterIcon from '@/icons/TwitterIcon';
import LinkedInIcon from '@/icons/LinkedInIcon';
import FooterHelpSupportIcon from '@/icons/FooterHelpSupportIcon';
import FooterContactIcon from '@/icons/FooterContactIcon';
import FooterLocationIcon from '@/icons/FooterLocationIcon';
import classNames from 'classnames';
import { type FooterGridProps } from './FooterGrid.types';

const FooterGrid: React.FC<FooterGridProps> = ({
  logoTitle,
  footerHelpAndSupportTitle,
  footerHelpAndSupportLink,
  contactUrl,
  stationLandingPageLink,
  primaryNav,
  secondaryNav,
  footerTertiaryMenu,
  facebookUserName,
  instagramUserName,
  twitterUserName,
  linkedInUserName,
  copyright,
}) => (
  <ThemeContext.Consumer>
    {({ brandLogo }) => (
      <footer className="footer">
        <div className="footer--top">
          <div className="footer__container container">
            <div className="footer__logo-container">
              <a className="footer__logo" href="/" aria-label="Go to Home page">
                {typeof brandLogo === 'string' ? (
                  <img src={brandLogo} alt={logoTitle} />
                ) : (
                  brandLogo
                )}
              </a>

              {footerHelpAndSupportTitle && (
                <a
                  className="footer__cta footer__cta--mobile button button--primary"
                  href={footerHelpAndSupportLink}
                >
                  {footerHelpAndSupportTitle}
                  <ButtonCircle
                    arrowHeight={13}
                    arrowWidth={17}
                    customIcon={<PinIcon />}
                  />
                </a>
              )}
            </div>
            <div className="footer__top_links">
              <ul className="footer__top_list">
                {footerHelpAndSupportTitle && (
                  <li className="footer__top_list-item footer__top_list-item--first">
                    <a
                      className="footer__top_list-link"
                      href={footerHelpAndSupportLink}
                    >
                      <FooterHelpSupportIcon width={27} height={23} />
                      <div className="footer__top_list-link--holder">
                        <span className="footer__top_list-link--label h5">
                          {footerHelpAndSupportTitle}
                        </span>
                        <span className="footer__top_list-link--helper">
                          Find answers to your questions
                        </span>
                      </div>
                    </a>
                  </li>
                )}

                {contactUrl && (
                  <li className="footer__top_list-item">
                    <a className="footer__top_list-link" href={contactUrl}>
                      <FooterContactIcon width={19} height={32} />
                      <div className="footer__top_list-link--holder">
                        <span className="footer__top_list-link--label h5">
                          Contact us
                        </span>
                        <span className="footer__top_list-link--helper">
                          Get in touch with our team
                        </span>
                      </div>
                    </a>
                  </li>
                )}

                {stationLandingPageLink && (
                  <li className="footer__top_list-item footer__top_list-item--last">
                    <a
                      className="footer__top_list-link"
                      href={stationLandingPageLink}
                    >
                      <FooterLocationIcon width={25} height={32} />
                      <div className="footer__top_list-link--holder">
                        <span className="footer__top_list-link--label h5">
                          Our locations
                        </span>
                        <span className="footer__top_list-link--helper">
                          Find your nearest Z station
                        </span>
                      </div>
                    </a>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
        <div className="footer--middle">
          <div className="footer__container container footer__container--spacer">
            <div className="footer__links">
              {primaryNav && (
                <ul className="footer__list footer__list--primary">
                  {primaryNav.map((page, index) => {
                    const { title, link, isNewWindow, displayOnlyOnMobile } =
                      page;

                    return (
                      <li className="footer__list-item" key={index}>
                        <a
                          href={link}
                          className={classNames('footer__list-link h5', {
                            'footer__list-link--mobile': displayOnlyOnMobile,
                          })}
                          target={isNewWindow ? '_blank' : '_self'}
                          rel="noreferrer"
                        >
                          {title}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              )}

              {secondaryNav && (
                <ul className="footer__list footer__list--secondary">
                  {secondaryNav.map((page, index) => {
                    const { title, link, isNewWindow, displayOnlyOnMobile } =
                      page;

                    return (
                      <li className="footer__list-item" key={index}>
                        <a
                          href={link}
                          className={classNames(
                            'footer__list-link footer__list-link--secondary',
                            {
                              'footer__list-link--mobile': displayOnlyOnMobile,
                            },
                          )}
                          target={isNewWindow ? '_blank' : '_self'}
                          rel="noreferrer"
                        >
                          {title}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
        <div className="footer--bottom">
          <div className="footer__container container footer__container--spacer">
            {footerTertiaryMenu && (
              <ul className="footer__utility">
                {footerTertiaryMenu.map((page, index) => {
                  const { title } = page;

                  return (
                    <li className="footer__utility-item" key={index}>
                      <a href="{$Link}" className="footer__utility-link">
                        {title}
                      </a>
                    </li>
                  );
                })}
              </ul>
            )}

            {(facebookUserName ||
              instagramUserName ||
              twitterUserName ||
              linkedInUserName) && (
              <ul className="footer__socials footer__socials--bottom">
                {facebookUserName && (
                  <li className="footer__social">
                    <a
                      href={`https://facebook.com/${facebookUserName}`}
                      className="footer__social-link"
                      target="_blank"
                      aria-label="Go to Facebook page"
                      rel="noreferrer"
                    >
                      <ThemeContext.Consumer>
                        {(theme) => (
                          <FacebookIcon
                            width={30}
                            height={30}
                            fill={theme.brandColor}
                          />
                        )}
                      </ThemeContext.Consumer>
                    </a>
                  </li>
                )}
                {instagramUserName && (
                  <li className="footer__social">
                    <a
                      href={`https://www.instagram.com/${instagramUserName}`}
                      className="footer__social-link"
                      target="_blank"
                      aria-label="Go to Instagram page"
                      rel="noreferrer"
                    >
                      <ThemeContext.Consumer>
                        {(theme) => (
                          <InstagramIcon
                            width={30}
                            height={30}
                            fill={theme.brandColor}
                          />
                        )}
                      </ThemeContext.Consumer>
                    </a>
                  </li>
                )}
                {twitterUserName && (
                  <li className="footer__social">
                    <a
                      href={`https://twitter.com/${twitterUserName}`}
                      className="footer__social-link"
                      target="_blank"
                      aria-label="Go to Twitter page"
                      rel="noreferrer"
                    >
                      <ThemeContext.Consumer>
                        {(theme) => (
                          <TwitterIcon
                            width={30}
                            height={30}
                            fill={theme.brandColor}
                          />
                        )}
                      </ThemeContext.Consumer>
                    </a>
                  </li>
                )}
                {linkedInUserName && (
                  <li className="footer__social">
                    <a
                      href={`https://linkedin.com/company/${linkedInUserName}`}
                      className="footer__social-link"
                      target="_blank"
                      aria-label="Go to Linkedin page"
                      rel="noreferrer"
                    >
                      <ThemeContext.Consumer>
                        {(theme) => (
                          <LinkedInIcon
                            width={30}
                            height={30}
                            fill={theme.brandColor}
                          />
                        )}
                      </ThemeContext.Consumer>
                    </a>
                  </li>
                )}
              </ul>
            )}
          </div>

          <div className="footer__container container footer__container--spacer">
            <div className="footer__bottom">
              <img
                alt="shielded"
                className="footer__shield"
                id="shielded-logo"
                src="https://shielded.co.nz/img/custom-logo.png"
                height="32"
                width="32"
              />
              {copyright && (
                <span className="footer__copyright">{copyright}</span>
              )}
            </div>
          </div>
        </div>
      </footer>
    )}
  </ThemeContext.Consumer>
);

export default FooterGrid;
